<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import KanbanAPI from 'dashboard/api/kanban';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const isEnabled = ref(false);
const waitMinutes = ref(10);
const batchSize = ref(5);
const delayMs = ref(200);
const prioritizeOnline = ref(true);
const selectedFunnelId = ref('');
const selectedAgents = ref([]);
const configuredAgentIds = ref([]);
const funnelOptions = ref([]);
const isSubmitting = ref(false);
const submitErrorMessage = ref('');
const isHydratingFromSettings = ref(false);

const agentOptions = computed(() => {
  return agents.value || [];
});

const parseAgentIds = value => {
  if (Array.isArray(value)) {
    return [...new Set(
      value
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
    )];
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return parseAgentIds(parsed);
    } catch {
      return raw
        .split(',')
        .map(id => Number(id.trim()))
        .filter(id => Number.isFinite(id) && id > 0);
    }
  }

  return [];
};

const selectedAgentIds = computed(() => {
  return [...new Set(
    (selectedAgents.value || [])
      .map(agent => Number(agent?.id))
      .filter(id => Number.isFinite(id) && id > 0)
  )];
});

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    isHydratingFromSettings.value = true;

    isEnabled.value = Boolean(settings.queue_redistribution_enabled);
    waitMinutes.value = Number(settings.queue_redistribution_wait_minutes || 10);
    batchSize.value = Number(settings.queue_redistribution_batch_size || 5);
    delayMs.value = Number(settings.queue_redistribution_delay_ms || 200);
    prioritizeOnline.value = Boolean(
      settings.queue_redistribution_prioritize_online
    );
    configuredAgentIds.value = parseAgentIds(
      settings.queue_redistribution_agent_ids
    );
    selectedFunnelId.value = settings.queue_redistribution_funnel_id
      ? String(settings.queue_redistribution_funnel_id)
      : '';
    submitErrorMessage.value = '';
  },
  { immediate: true, deep: true }
);

watch(
  [agentOptions, configuredAgentIds],
  ([newAgentOptions, newConfiguredAgentIds]) => {
    if (!isHydratingFromSettings.value) {
      return;
    }

    if (!Array.isArray(newAgentOptions) || !newAgentOptions.length) {
      selectedAgents.value = [];
      return;
    }

    if (!Array.isArray(newConfiguredAgentIds) || !newConfiguredAgentIds.length) {
      selectedAgents.value = [];
      isHydratingFromSettings.value = false;
      return;
    }

    const selectedIds = new Set(newConfiguredAgentIds.map(id => Number(id)));
    selectedAgents.value = newAgentOptions.filter(agent =>
      selectedIds.has(Number(agent.id))
    );
    isHydratingFromSettings.value = false;
  },
  { immediate: true, deep: true }
);

const fetchFunnels = async () => {
  try {
    const response = await KanbanAPI.getFunnels();
    const payload = Array(response?.data) ? response.data : [];
    funnelOptions.value = payload.map(funnel => ({
      id: funnel.id,
      name: funnel.name,
    }));
  } catch (error) {
    funnelOptions.value = [];
  }
};

onMounted(async () => {
  await Promise.all([store.dispatch('agents/get'), fetchFunnels()]);
});

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    submitErrorMessage.value = '';
    await updateAccount(settings, { silent: true });
    await store.dispatch('accounts/get');
    useAlert(
      t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.WAIT_MINUTES.API.SUCCESS')
    );
  } catch (error) {
    const serverErrorMessage =
      error?.response?.data?.error ||
      t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.WAIT_MINUTES.API.ERROR');
    submitErrorMessage.value = serverErrorMessage;
    useAlert(
      serverErrorMessage
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (waitMinutes.value < 1 || waitMinutes.value > 10080) {
    useAlert(
      t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.WAIT_MINUTES.ERROR')
    );
    return Promise.resolve();
  }
  if (batchSize.value < 1 || batchSize.value > 1000) {
    useAlert(
      t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.BATCH_SIZE.ERROR')
    );
    return Promise.resolve();
  }
  if (delayMs.value < 0 || delayMs.value > 60000) {
    useAlert(
      t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.DELAY_MS.ERROR')
    );
    return Promise.resolve();
  }
  if (!selectedFunnelId.value) {
    useAlert(t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.FUNNEL.ERROR'));
    return Promise.resolve();
  }
  if (!selectedAgentIds.value.length) {
    useAlert(t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.AGENTS.ERROR'));
    return Promise.resolve();
  }

  return updateAccountSettings({
    queue_redistribution_enabled: true,
    queue_redistribution_wait_minutes: waitMinutes.value,
    queue_redistribution_batch_size: batchSize.value,
    queue_redistribution_delay_ms: delayMs.value,
    queue_redistribution_prioritize_online: prioritizeOnline.value,
    queue_redistribution_agent_ids: selectedAgentIds.value,
    queue_redistribution_funnel_id: Number(selectedFunnelId.value),
  });
};

const handleDisable = async () => {
  return updateAccountSettings({
    queue_redistribution_enabled: false,
    queue_redistribution_wait_minutes: waitMinutes.value || 10,
    queue_redistribution_batch_size: batchSize.value || 5,
    queue_redistribution_delay_ms: delayMs.value || 200,
    queue_redistribution_prioritize_online: prioritizeOnline.value,
    queue_redistribution_agent_ids: selectedAgentIds.value,
    queue_redistribution_funnel_id: selectedFunnelId.value
      ? Number(selectedFunnelId.value)
      : null,
  });
};

const toggleQueueRedistribution = async () => {
  if (!isEnabled.value) {
    await handleDisable();
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleQueueRedistribution" />
        </div>
      </div>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.FUNNEL.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.FUNNEL.HELP')
          "
        >
          <select v-model="selectedFunnelId" class="!mb-0 text-sm">
            <option value="">
              {{
                t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.FUNNEL.PLACEHOLDER')
              }}
            </option>
            <option
              v-for="funnel in funnelOptions"
              :key="funnel.id"
              :value="String(funnel.id)"
            >
              {{ funnel.name }}
            </option>
          </select>
        </WithLabel>

        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.AGENTS.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.AGENTS.HELP')
          "
        >
          <multiselect
            v-model="selectedAgents"
            :options="agentOptions"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.AGENTS.PLACEHOLDER')
            "
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            selected-label
            hide-selected
          />
        </WithLabel>

        <WithLabel
          :label="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.WAIT_MINUTES.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.WAIT_MINUTES.HELP')
          "
        >
          <NextInput
            v-model.number="waitMinutes"
            class="w-full"
            type="number"
            min="1"
            max="10080"
          />
        </WithLabel>

        <WithLabel
          :label="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.BATCH_SIZE.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.BATCH_SIZE.HELP')
          "
        >
          <NextInput
            v-model.number="batchSize"
            class="w-full"
            type="number"
            min="1"
            max="1000"
          />
        </WithLabel>

        <WithLabel
          :label="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.DELAY_MS.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.DELAY_MS.HELP')
          "
        >
          <NextInput
            v-model.number="delayMs"
            class="w-full"
            type="number"
            min="0"
            max="60000"
          />
        </WithLabel>

        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.PREFERENCES')"
        >
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
          >
            <div class="p-3 h-12 flex items-center justify-between">
              <span>
                {{
                  t(
                    'GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.PRIORITIZE_ONLINE.LABEL'
                  )
                }}
              </span>
              <Switch v-model="prioritizeOnline" />
            </div>
          </div>
        </WithLabel>

        <p v-if="submitErrorMessage" class="mb-0 text-sm text-ruby-11">
          {{ submitErrorMessage }}
        </p>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="
              t('GENERAL_SETTINGS.FORM.QUEUE_REDISTRIBUTION.UPDATE_BUTTON')
            "
          />
        </div>
      </form>
    </div>
  </div>
</template>
